import styled from '@emotion/styled';
import {
  Row,
  Column,
  device,
  typography,
  useMedia,
  Reveal,
  mobileGutterMargin,
  shouldForwardProp,
} from '@landsbankinn/ui';
import React from 'react';

interface IProps {
  title?: React.ReactNode;
  copy?: React.ReactNode;
  images: React.ReactNode;
  align?: 'Default' | 'Left' | 'Right';
  mergeTop?: boolean;
  link?: React.ReactNode;
  link2?: React.ReactNode;
}

const Host = styled<any>(Reveal, {
  shouldForwardProp: shouldForwardProp('div'),
})``;

const ImageWrap = styled.div`
  position: relative;
  overflow: hidden;
  margin: ${(props) => `0 -${props.theme.containerGutter}px`};
  margin-bottom: 2em;

  ${mobileGutterMargin}
  @media ${device.mobile} {
    margin-bottom: 2em;
  }

  @media ${device.tablet} {
    position: sticky;
    top: 120px;
    margin: 0;
  }

  @media ${device.desktop} {
    position: relative;
    top: 0;
  }
`;

const Content = styled.div<{ hasPadding: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  justify-content: center;

  height: 100%;

  @media ${device.desktop} {
    padding-right: 35px;
    ${(props) => props.hasPadding && 'padding: 0 35px;'}
  }
`;

const Title = styled.h2<{ hasLink: boolean }>`
  ${typography.h3}
  ${(props) => props.hasLink && 'margin-top: auto;'}
  margin-bottom: calc((44 / ${typography.sizes.h3Size}) * 1em);
  max-width: 75%;

  @media ${device.desktop} {
    margin-bottom: 44px;
  }
`;

const Copy = styled.div`
  margin-bottom: 30px;
`;

const Links = styled.div`
  display: flex;
  flex-flow: row wrap;

  width: 100%;
  margin-top: 80px;
  margin-top: auto;

  border-top: ${(props) => `1px solid ${props.theme.colors.primary}`};

  > a {
    width: auto;
    flex: 0 0 auto;
    margin-right: 30px;
    margin-top: 20px;

    &:last-child {
      margin-right: 0;
    }
  }
`;

export const TextWithImages = ({
  title,
  copy,
  images,
  align,
  mergeTop,
  link,
  link2,
}: IProps) => {
  const hasLink = !!link;
  const isDefault = align === 'Default';
  const isRight = align === 'Right';
  const isTablet = useMedia('md');

  return (
    <Host mergeTop={mergeTop}>
      <Row reverse={align === 'Right'}>
        <Column
          md={{
            width: isDefault && !isTablet ? 7 / 12 : 6 / 12,
            offset: isRight && !isTablet ? 1 / 12 : 0,
          }}
          lg={{
            width: isDefault ? 7 / 12 : 6 / 12,
            offset: isRight ? 1 / 12 : 0,
          }}
        >
          <ImageWrap>{images}</ImageWrap>
        </Column>

        <Column
          md={{
            width: isTablet ? 6 / 12 : 5 / 12,
            offset: isDefault || isRight || isTablet ? 0 : 1 / 12,
          }}
          lg={{
            width: 5 / 12,
            offset: isDefault || isRight ? 0 : 1 / 12,
          }}
        >
          <Content hasPadding={isDefault}>
            {!!title && <Title hasLink={hasLink}>{title}</Title>}
            <Copy>{copy}</Copy>
            {hasLink && (
              <Links>
                {link}
                {link2}
              </Links>
            )}
          </Content>
        </Column>
      </Row>
    </Host>
  );
};
